import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Textarea } from '@/components/Textarea';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Modal } from '@/components/Modal';
import { contactsService, ContactEmailThread } from '@/services/contacts.service';
import { botFlowsService, BotFlow } from '@/services/bot-flows.service';
import { api } from '@/config/api';
import { ActivityTimeline } from '@/components/ActivityTimeline';
import { NotesTab } from '@/components/NotesTab';
import { CustomFieldsForm } from '@/components/CustomFieldsForm';
import { ContactCustomRecords } from '@/components/ContactCustomRecords';
import { User, Mail, Phone, MapPin, Building2, StickyNote, Tag, Trash2, Bot, Activity as ActivityIcon, Settings2, ShieldOff, ArrowLeft } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useLabels } from '@/contexts/WorkspaceContext';
import { useModuleAccess } from '@/contexts/ModuleAccessContext';
import { CustomerProfile } from '@/components/contacts/CustomerProfile';
import { ContactGroupsModal } from '@/components/contacts/ContactGroupsModal';
import { contactGroupsService, ContactGroup } from '@/services/contact-groups.service';

function InitialsAvatar({ name, size = 'lg' }: { name: string; size?: 'lg' | 'xl' }) {
  const initials = name
    .trim()
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  const sz = size === 'xl' ? 'h-16 w-16 text-2xl' : 'h-12 w-12 text-lg';
  return (
    <div className={clsx(
      'flex shrink-0 items-center justify-center rounded-2xl bg-gold-gradient text-on-primary font-bold shadow-glow',
      sz,
    )}>
      {initials || <User size={20} />}
    </div>
  );
}

export default function ContactDetail() {
  const t = useLabels();
  const { has } = useModuleAccess();
  // The list's Edit button opens straight on the editable tab.
  const [search] = useSearchParams();
  const [tab, setTab] = useState<'overview' | 'details'>(search.get('tab') === 'details' ? 'details' : 'overview');
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [allGroups, setAllGroups] = useState<ContactGroup[]>([]);
  const { uid } = useParams<{ uid: string }>();
  const nav = useNavigate();
  const [contact, setContact] = useState<any>(null);
  const [del, setDel] = useState(false);
  const [botModalOpen, setBotModalOpen] = useState(false);
  const [flows, setFlows] = useState<BotFlow[]>([]);
  const [consentAction, setConsentAction] = useState<boolean | null>(null);
  const [consentNote, setConsentNote] = useState('');
  const [savingConsent, setSavingConsent] = useState(false);
  const [triggering, setTriggering] = useState<string | null>(null);

  // Last server copy — a blur that changed nothing must not save (or toast "Saved").
  const savedRef = useRef<any>(null);
  const refresh = () => contactsService.get(uid!).then((c) => { savedRef.current = c; setContact(c); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [uid]);

  // Load first — opening on an empty list flashes "No groups yet".
  const openGroups = async () => {
    try { setAllGroups(await contactGroupsService.list()); setGroupsOpen(true); } catch { /* interceptor */ }
  };

  const openBotModal = async () => {
    setBotModalOpen(true);
    if (flows.length === 0) {
      try {
        const list = await botFlowsService.list();
        setFlows(list.filter((f) => f.isActive));
      } catch { /* interceptor */ }
    }
  };

  const triggerFlow = async (flowUid: string) => {
    if (!uid) return;
    setTriggering(flowUid);
    try {
      const r = await api.post(`/slighter_crm/bot-flows/${flowUid}/trigger`, { contactUid: uid });
      const data = r.data as { ok: boolean; sentBody: string; error?: string };
      if (data.ok) {
        toast.success(`Bot triggered — "${data.sentBody.slice(0, 40)}..." sent`);
        setBotModalOpen(false);
      } else {
        toast.error(data.error ?? 'Failed to trigger bot');
      }
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setTriggering(null); }
  };

  const save = async (patch: any) => {
    const prev = savedRef.current ?? {};
    const changed = Object.keys(patch).some((k) => JSON.stringify(patch[k] ?? '') !== JSON.stringify(prev[k] ?? ''));
    if (!changed) return;
    try {
      await contactsService.update(uid!, patch);
      toast.success('Saved');
      await refresh();
    } catch { await refresh().catch(() => undefined); }
  };
  const recordConsent = async () => {
    if (!uid || consentAction === null) return;
    setSavingConsent(true);
    try {
      await contactsService.setWhatsappConsent(uid, consentAction, consentNote);
      await refresh(); setConsentAction(null); setConsentNote('');
      toast.success(consentAction ? 'WhatsApp consent recorded' : 'WhatsApp consent withdrawn');
    } catch { /* API interceptor displays the error. */ }
    finally { setSavingConsent(false); }
  };
  const remove = async () => {
    await contactsService.remove(uid!);
    toast.success('Deleted');
    nav('/contacts');
  };

  if (!contact) {
    return (
      <>
        <Topbar title={t('contacts', 'singular')} breadcrumb={`Audience / ${t('contacts')}`} />
        <div className="px-7 py-7 w-full space-y-4">
          <div className="skeleton h-28 rounded-2xl" />
          <div className="grid grid-cols-2 gap-4">
            <div className="skeleton h-64 rounded-2xl" />
            <div className="skeleton h-64 rounded-2xl" />
          </div>
        </div>
      </>
    );
  }

  const fullName = `${contact.firstName} ${contact.lastName ?? ''}`.trim();

  return (
    <>
      <Topbar
        title={fullName}
        breadcrumb={`Audience / ${t('contacts')}`}
        back={
          <button
            onClick={() => nav('/contacts')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
            aria-label="Back to contacts"
          >
            <ArrowLeft size={16} />
          </button>
        }
        right={
          <div className="flex gap-2">
            {has('whatsapp_bot') && <Button variant="secondary" iconLeft={<Bot size={14} />} onClick={openBotModal} disabled={!contact.phone} title={!contact.phone ? 'Add a phone number to start a WhatsApp bot' : undefined}>
              Trigger bot
            </Button>}
            <Button variant="danger" iconLeft={<Trash2 size={14} />} onClick={() => setDel(true)}>
              Delete
            </Button>
          </div>
        }
      />

      <div className="px-7 py-7 w-full">
        {/* Hero card */}
        <Card className="mb-5">
          <div className="flex items-center gap-4">
            <InitialsAvatar name={fullName} size="xl" />
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-xl font-bold tracking-tight text-text-primary">{fullName}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {contact.phone && (
                  <span className="inline-flex items-center gap-1 text-xs text-text-muted">
                    <Phone size={11} /> <span className="font-mono">{contact.phone}</span>
                  </span>
                )}
                {contact.email && (
                  <span className="inline-flex items-center gap-1 text-xs text-text-muted">
                    <Mail size={11} /> {contact.email}
                  </span>
                )}
                {contact.optedIn && contact.whatsappConsentAt && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-xs font-semibold text-success">
                    <span className="h-1.5 w-1.5 rounded-full bg-success" /> WhatsApp consent recorded
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>

        {(has('whatsapp_templates') || has('whatsapp_bulk') || has('whatsapp_inbox')) && <Card className="mb-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-text-primary">WhatsApp marketing consent</h3>
              <p className="mt-1 text-sm text-text-muted">{contact.optedIn && contact.whatsappConsentAt
                ? `Recorded ${new Date(contact.whatsappConsentAt).toLocaleString()} for ${contact.whatsappConsentPhone}.`
                : contact.optedOutAt ? 'Consent was withdrawn. Marketing is blocked until the customer gives fresh consent.' : 'No consent recorded yet. Recording it keeps a note of when and how the customer agreed.'}</p>
              {contact.whatsappConsentNote && <p className="mt-2 text-xs text-text-secondary">{contact.whatsappConsentNote}</p>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => { setConsentAction(false); setConsentNote(''); }}>Record withdrawal</Button>
              <Button size="sm" disabled={!contact.phone && !contact.whatsappPhone} onClick={() => { setConsentAction(true); setConsentNote(''); }}>Record consent</Button>
            </div>
          </div>
        </Card>}

        <div className="mb-5 flex gap-5 border-b border-border" aria-label="Customer profile views">
          {(['overview', 'details'] as const).map(value => <button key={value} onClick={() => setTab(value)} aria-current={tab === value ? 'page' : undefined} className={clsx('border-b-2 pb-3 text-sm font-semibold', tab === value ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text-primary')}>{value === 'overview' ? 'Customer overview' : 'Contact details'}</button>)}
        </div>
        {tab === 'overview' ? <CustomerProfile key={uid} contactUid={uid!} /> : <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Contact info */}
          <Card>
            <SectionHeader title={`${t('contacts', 'singular')} info`} icon={<User size={16} />} className="mb-4" />
            <FormField label="First name">
              <Input
                value={contact.firstName}
                onChange={(e) => setContact({ ...contact, firstName: e.target.value })}
                onBlur={() => save({ firstName: contact.firstName })}
              />
            </FormField>
            <FormField label="Last name">
              <Input
                value={contact.lastName ?? ''}
                onChange={(e) => setContact({ ...contact, lastName: e.target.value })}
                onBlur={() => save({ lastName: contact.lastName })}
              />
            </FormField>
            <FormField label="Phone">
              <Input type="tel" value={contact.phone ?? ''} placeholder="+971 50 123 4567" onChange={(event) => setContact({ ...contact, phone: event.target.value })} onBlur={() => save({ phone: contact.phone })} />
            </FormField>
            <FormField label="Email">
              <Input
                type="email"
                value={contact.email ?? ''}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                onBlur={() => save({ email: contact.email })}
              />
            </FormField>
          </Card>

          {/* Company + notes */}
          <div className="space-y-4">
            <Card>
              <SectionHeader title="Company" icon={<Building2 size={16} />} className="mb-4" />
              <FormField label="Company">
                <Input
                  value={contact.company ?? ''}
                  onChange={(e) => setContact({ ...contact, company: e.target.value })}
                  onBlur={() => save({ company: contact.company })}
                  iconLeft={<Building2 size={14} />}
                />
              </FormField>
              <FormField label="City">
                <Input
                  value={contact.city ?? ''}
                  onChange={(e) => setContact({ ...contact, city: e.target.value })}
                  onBlur={() => save({ city: contact.city })}
                  iconLeft={<MapPin size={14} />}
                />
              </FormField>
            </Card>

          </div>

          {/* Groups */}
          <Card className="lg:col-span-2">
            <SectionHeader
              title="Groups"
              icon={<Tag size={16} />}
              className="mb-4"
              action={<Button size="sm" variant="secondary" onClick={openGroups}>Manage</Button>}
            />
            {contact.groups.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-6 text-text-muted">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-alt">
                  <Tag size={16} />
                </div>
                <span className="text-sm">Not in any group.</span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {contact.groups.map((g: any) => (
                  <span
                    key={g.groupUid}
                    className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold"
                    style={{
                      background: `${g.color}18`,
                      color: g.color,
                      borderColor: `${g.color}30`,
                    }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: g.color }} />
                    {g.name}
                  </span>
                ))}
              </div>
            )}
          </Card>

          {/* Privacy & consent — channel-specific opt-outs (Meta share, etc.) */}
          <Card className="lg:col-span-2">
            <SectionHeader title="Privacy & consent" icon={<ShieldOff size={16} />} className="mb-3" />
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary-soft"
                checked={!!contact.metaShareOptedOut}
                onChange={(e) => {
                  const next = e.target.checked;
                  setContact({ ...contact, metaShareOptedOut: next });
                  save({ metaShareOptedOut: next });
                }}
              />
              <div className="flex-1">
                <span className="text-sm font-semibold text-text-primary">Don't share with Meta</span>
                <p className="mt-0.5 text-xs text-text-muted">
                  When ticked, this contact's email + phone are excluded from
                  Facebook/Instagram Custom Audience uploads (used for ad
                  targeting + lookalikes). General in-CRM use is unaffected.
                  Required for GDPR / CCPA when a person opts out of ad sharing.
                </p>
              </div>
            </label>
          </Card>

          {/* Custom fields (renders nothing if no definitions exist) */}
          <Card className="lg:col-span-2">
            <SectionHeader title="Custom fields" icon={<Settings2 size={16} />} className="mb-4" />
            <CustomFieldsForm
              entityType="contact"
              values={contact.customFields ?? {}}
              onChange={(key, value) => {
                const next = { ...(contact.customFields ?? {}), [key]: value };
                setContact({ ...contact, customFields: next });
                save({ customFields: next });
              }}
            />
          </Card>

          {/* Related custom-object records (visits, projects, orders, ...) */}
          <div className="lg:col-span-2">
            {has('custom_objects') && <ContactCustomRecords contactUid={uid!} />}
          </div>

          {/* Activity timeline */}
          <Card className="lg:col-span-2">
            <SectionHeader title="Activity" icon={<ActivityIcon size={16} />} className="mb-4" />
            {has('calendar') && <ActivityTimeline entityType="contact" entityUid={uid!} />}
          </Card>

          {/* Notes (multi-note tab — replaces the old single-line note) */}
          <Card className="lg:col-span-2">
            <SectionHeader title="Notes" icon={<StickyNote size={16} />} className="mb-4" />
            <NotesTab entityType="contact" entityUid={uid!} />
          </Card>

          {/* Per-contact email thread */}
          <Card className="lg:col-span-2">
            <SectionHeader title="Emails" icon={<Mail size={16} />} className="mb-4" />
            <ContactEmailThreadPanel contactUid={uid!} />
          </Card>
        </div>}
      </div>

      <ContactGroupsModal
        open={groupsOpen}
        onClose={() => setGroupsOpen(false)}
        targets={[contact]}
        groups={allGroups}
        onGroupCreated={(g) => setAllGroups((prev) => [...prev, g])}
        onSaved={refresh}
      />

      <ConfirmDialog
        open={del}
        title={`Delete ${t('contacts', 'singular').toLowerCase()}?`}
        message={`${contact.firstName} ${contact.lastName ?? ''} will be removed permanently.`}
        danger
        onConfirm={remove}
        onCancel={() => setDel(false)}
      />

      <Modal open={consentAction !== null} onClose={() => { if (!savingConsent) setConsentAction(null); }} title={consentAction ? 'Record WhatsApp consent' : 'Record consent withdrawal'} widthClass="max-w-lg">
        <div className="space-y-4">
          <p className="text-sm text-text-muted">{consentAction ? 'Confirm that this customer agreed to receive WhatsApp marketing at this number. Recording fresh consent also clears its earlier WhatsApp opt-out.' : 'Marketing and automated broadcasts will stop for this customer. Their conversation history remains available.'}</p>
          <FormField label={consentAction ? 'How and when was consent received?' : 'How was the withdrawal received?'}>
            <Textarea value={consentNote} maxLength={500} rows={3} onChange={event => setConsentNote(event.target.value)} placeholder="Source, date and any reference to the customer’s request" />
          </FormField>
          <div className="flex justify-end gap-2"><Button variant="secondary" disabled={savingConsent} onClick={() => setConsentAction(null)}>Cancel</Button><Button loading={savingConsent} disabled={consentNote.trim().length < 3} onClick={recordConsent}>{consentAction ? 'Confirm consent' : 'Withdraw consent'}</Button></div>
        </div>
      </Modal>
      <Modal open={botModalOpen} onClose={() => setBotModalOpen(false)} title="Trigger a bot flow" widthClass="max-w-md">
        <div className="px-6 py-5">
          <p className="mb-4 text-sm text-text-secondary">
            Select an active bot flow to send to <strong>{fullName}</strong>. The flow's first message will be sent now via your default WhatsApp account.
          </p>
          {flows.length === 0 && (
            <div className="rounded-lg border border-border bg-surface-alt/40 p-4 text-center text-sm text-text-muted">
              No active bot flows. Create one in Leads → Bot Flows first.
            </div>
          )}
          <div className="space-y-2">
            {flows.map((f) => (
              <button
                key={f.flowUid}
                onClick={() => triggerFlow(f.flowUid)}
                disabled={triggering !== null}
                className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5 text-left hover:border-primary/40 hover:bg-surface-alt disabled:cursor-wait disabled:opacity-60"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                  <Bot size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-text-primary">{f.name}</div>
                  {f.description && <div className="truncate text-xs text-text-muted">{f.description}</div>}
                </div>
                {triggering === f.flowUid && <span className="text-2xs text-text-muted">Sending…</span>}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" onClick={() => setBotModalOpen(false)} disabled={triggering !== null}>Close</Button>
        </div>
      </Modal>
    </>
  );
}

// ── Per-contact email thread (mirrors the per-deal pattern) ─────────────────
function ContactEmailThreadPanel({ contactUid }: { contactUid: string }) {
  const [thread, setThread] = useState<ContactEmailThread | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    contactsService.emails(contactUid)
      .then(setThread)
      .catch(() => setThread({ outbound: [], inbound: [] }))
      .finally(() => setLoading(false));
  }, [contactUid]);

  if (loading) return <div className="text-xs text-text-muted">Loading…</div>;
  if (!thread) return null;

  const merged = [
    ...thread.outbound.map((m) => ({ ...m, t: m.sentAt ?? m.createdAt })),
    ...thread.inbound.map((m) => ({ ...m, t: m.createdAt })),
  ].sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());

  if (merged.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-4 py-6 text-center text-xs text-text-muted">
        No emails yet. Sent and received emails involving this contact will show up here.
      </div>
    );
  }

  return (
    <div className="max-h-[420px] overflow-y-auto pr-1 space-y-2">
      {merged.map((m, i) => (
        <div
          key={`${m.direction}-${m.messageUid ?? m.eventUid ?? i}`}
          className={`rounded-lg border px-3 py-2.5 text-xs ${
            m.direction === 'outbound' ? 'border-primary/30 bg-primary-soft/30' : 'border-border bg-surface'
          }`}
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 font-semibold text-text-primary">
              {m.direction === 'outbound'
                ? <><Mail size={11} className="text-primary" /> To {m.toEmail}</>
                : <><Mail size={11} className="text-text-muted" /> From {m.from}</>}
            </span>
            <span className="text-2xs text-text-muted">{new Date(m.t).toLocaleString()}</span>
          </div>
          <div className="font-medium text-text-primary mb-1">{m.subject ?? '(no subject)'}</div>
          {m.direction === 'outbound' && (
            <div className="flex flex-wrap items-center gap-1.5 text-2xs">
              <span className="rounded-full bg-surface-alt px-2 py-0.5 font-semibold uppercase text-text-secondary">{m.status ?? 'queued'}</span>
              {(m.openCount ?? 0) > 0 && <span className="text-success">{m.openCount}× opened</span>}
              {(m.clickCount ?? 0) > 0 && <span className="text-info">{m.clickCount}× clicked</span>}
            </div>
          )}
          {m.direction === 'inbound' && m.snippet && (
            <div className="text-text-muted mt-1 line-clamp-3">{m.snippet}</div>
          )}
        </div>
      ))}
    </div>
  );
}
